import React, { useState } from "react";
import { motion } from "framer-motion";
import { Star, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`review-star-${n}`}
          onClick={() => onChange?.(n)}
          aria-label={`${n} sterren`}
          className="transition-transform hover:scale-110 focus:outline-none"
        >
          <Star size={onChange ? 26 : 16} className={n <= value ? "fill-sfc-yellow text-sfc-yellow" : "text-white/25"} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews({ reviews = [], onSubmitted }) {
  const [form, setForm] = useState({ name: "", company_name: "", email: "", rating: 5, text: "", consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Geef toestemming voor publicatie.");
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", form);
      toast.success(data.message || "Bedankt voor uw review!");
      setForm({ name: "", company_name: "", email: "", rating: 5, text: "", consent: false, website: "" });
      setOpen(false);
      onSubmitted?.();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const loop = reviews.length ? [...reviews, ...reviews] : [];

  return (
    <section id="reviews" className="relative overflow-hidden bg-[#120726] px-5 py-28 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-sfc-cyan">Reviews</span>
              <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                Wat onze klanten zeggen
              </h2>
            </div>
            <button
              data-testid="open-review-form"
              onClick={() => setOpen((v) => !v)}
              className="rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              {open ? "Sluiten" : "Schrijf een review"}
            </button>
          </div>
        </Reveal>

        {open && (
          <form data-testid="review-form" onSubmit={submit} className="mt-8 grid gap-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:grid-cols-2">
            <input aria-hidden="true" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" />
            <input data-testid="review-name" required placeholder="Naam" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border border-white/10 bg-sfc-night px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-sfc-orange focus:outline-none" />
            <input data-testid="review-company" placeholder="Bedrijfsnaam (optioneel)" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-xl border border-white/10 bg-sfc-night px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-sfc-orange focus:outline-none" />
            <input data-testid="review-email" required type="email" placeholder="E-mailadres" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border border-white/10 bg-sfc-night px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-sfc-orange focus:outline-none sm:col-span-2" />
            <div className="sm:col-span-2"><Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} /></div>
            <textarea data-testid="review-text" required rows={3} placeholder="Uw ervaring..." value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="rounded-xl border border-white/10 bg-sfc-night px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-sfc-orange focus:outline-none sm:col-span-2" />
            <label className="flex items-center gap-2 text-sm text-white/70 sm:col-span-2">
              <input data-testid="review-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="h-4 w-4 accent-sfc-orange" />
              Ik geef toestemming om deze review te publiceren.
            </label>
            <button data-testid="review-submit" disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-sfc-orange to-sfc-red px-6 py-3 text-sm font-semibold text-white disabled:opacity-60 sm:col-span-2">
              {busy && <Loader2 className="animate-spin" size={16} />} Review versturen
            </button>
          </form>
        )}

        {reviews.length === 0 ? (
          <p className="mt-12 text-white/50">Nog geen reviews. Wees de eerste!</p>
        ) : (
          <div className="mt-14 flex gap-6 overflow-hidden" style={{ maskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)" }}>
            <motion.div
              className="flex gap-6"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: Math.max(18, reviews.length * 8), repeat: Infinity, ease: "linear" }}
            >
              {loop.map((r, i) => (
                <article key={i} data-testid={i < reviews.length ? `review-card-${i}` : undefined} className="w-[320px] shrink-0 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                  <Stars value={r.rating} />
                  <p className="mt-4 text-sm leading-relaxed text-white/80">“{r.text}”</p>
                  <div className="mt-5 border-t border-white/10 pt-4">
                    <p className="font-display text-sm text-white">{r.name}</p>
                    {r.company_name && <p className="text-xs text-white/50">{r.company_name}</p>}
                  </div>
                </article>
              ))}
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
}
